import { captureMessage } from "@sentry/react";
import { memo, useEffect, useMemo, useRef } from "react";
import { useApp } from "@/hooks/useApp";

export const Banner = memo(function Banner() {
	const { audience } = useApp();
	const containerRef = useRef<HTMLDivElement>(null);
	const banner = useMemo(() => {
		const check = window as Window & {
			clearingBanners?: { home: string; international: string };
		};

		if (!check.clearingBanners) {
			captureMessage(
				"No banners found in window.clearingBanners. No clearing banner shown",
				"error",
			);
			return {
				__html: "",
			};
		}
		if (audience && !check.clearingBanners[audience]) {
			captureMessage(
				`No banners found for ${audience}. Reverting to home`,
				"error",
			);
			return {
				__html: check.clearingBanners.home.replace(/RM_INC/g, "script"),
			};
		}
		if (!audience || audience === "home")
			return {
				__html: check.clearingBanners.home.replace(/RM_INC/g, "script"),
			};
		if (audience === "international") {
			return {
				__html: check.clearingBanners.international.replace(
					/RM_INC/g,
					"script",
				),
			};
		}

		return {
			__html: "",
		};
	}, [audience]);

	useEffect(() => {
		if (containerRef.current) {
			const scripts = containerRef.current.querySelectorAll("script");
			scripts.forEach((oldScript) => {
				const newScript = document.createElement("script");
				Array.from(oldScript.attributes).forEach((attr) => {
					newScript.setAttribute(attr.name, attr.value);
				});
				newScript.textContent = oldScript.textContent;
				oldScript.parentNode?.replaceChild(newScript, oldScript);
			});
		}
	}, []);

	// biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is from a trusted source and needs to be injected as-is
	return <div ref={containerRef} dangerouslySetInnerHTML={banner} />;
});
